import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { SalonProvider } from './context/SalonContext';
import ClientLayout from './components/ClientLayout';
import ProLayout from './components/ProLayout';
import AdminLayout from './components/AdminLayout';
import ErrorBoundary from './components/ErrorBoundary';
import AndroidBackButtonHandler from './components/AndroidBackButtonHandler';
import PWAInstallBanner from './components/PWAInstallBanner';

// ── Lazy-loaded Public Pages ───────────────────────────────────────────────
const Home           = lazy(() => import('./pages/Home'));
const Login          = lazy(() => import('./pages/Login'));
const Register       = lazy(() => import('./pages/Register'));
const VerifyEmail    = lazy(() => import('./pages/VerifyEmail'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword  = lazy(() => import('./pages/ResetPassword'));
const SearchResults  = lazy(() => import('./pages/SearchResults'));
const SalonDetails   = lazy(() => import('./pages/SalonDetails'));
const CategoryResult = lazy(() => import('./pages/CategoryResult'));
const ProPage        = lazy(() => import('./pages/ProPage'));
const Pricing        = lazy(() => import('./pages/Pricing'));
const NotFound       = lazy(() => import('./pages/NotFound'));
const PrivacyPolicy  = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));


// ── Lazy-loaded Client Pages ───────────────────────────────────────────────
const Dashboard    = lazy(() => import('./pages/Dashboard'));
const Services     = lazy(() => import('./pages/Services'));
const Appointments = lazy(() => import('./pages/Appointments'));
const Profile      = lazy(() => import('./pages/Profile'));
const Rewards      = lazy(() => import('./pages/client/Rewards'));
const PaymentPage = lazy(() => import('./pages/client/PaymentPage'));
const PaymentSuccess = lazy(() => import('./pages/client/PaymentSuccess'));
const PaymentSimulate = lazy(() => import('./pages/client/PaymentSimulate'));
const PaymentHistory = lazy(() => import('./pages/client/PaymentHistory'));
const ClientNotifications = lazy(() => import('./pages/client/ClientNotifications'));
const ReviewPage = lazy(() => import('./pages/client/ReviewPage'));

// ── Lazy-loaded Pro Pages ─────────────────────────────────────────────────
const MySalons        = lazy(() => import('./pages/pro/MySalons'));
const MyServices      = lazy(() => import('./pages/pro/MyServices'));
const ProAppointments = lazy(() => import('./pages/pro/ProAppointments'));
const ProClients      = lazy(() => import('./pages/pro/ProClients'));
const ProDashboard    = lazy(() => import('./pages/pro/ProDashboard'));
const ProEmployees    = lazy(() => import('./pages/pro/ProEmployees'));
const ProVerificationStatus = lazy(() => import('./pages/pro/ProVerificationStatus'));
const ProPaymentSettings = lazy(() => import('./pages/pro/ProPaymentSettings'));
const ProNotifications   = lazy(() => import('./pages/pro/ProNotifications'));
const ProReviews         = lazy(() => import('./pages/pro/ProReviews'));
// ── Lazy-loaded Admin Pages ───────────────────────────────────────────────
const AdminSalons = lazy(() => import('./pages/admin/AdminSalons'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminAppointments = lazy(() => import('./pages/admin/AdminAppointments'));
const AdminPayments = lazy(() => import('./pages/admin/AdminPayments'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports'));
const AdminMarketing = lazy(() => import('./pages/admin/AdminMarketing'));
const AdminAuditLogs = lazy(() => import('./pages/admin/AdminAuditLogs'));

// ── Global Page Loader ────────────────────────────────────────────────────
const PageLoader = () => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-reglow-bg">
    <div className="flex flex-col items-center animate-pulse gap-6 w-full max-w-sm px-6">
      {/* Brand skeleton */}
      <div className="h-8 w-32 bg-gray-200 rounded-lg mb-8" />
      {/* Cards skeleton */}
      <div className="w-full h-48 bg-gray-200 rounded-2xl" />
      <div className="w-full h-16 bg-gray-200 rounded-xl" />
      <div className="w-full h-16 bg-gray-200 rounded-xl" />
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <SalonProvider>
        <ErrorBoundary>
          <AndroidBackButtonHandler />
          <PWAInstallBanner />
          <div style={{ minHeight: '100vh', backgroundColor: '#F8F7F5' }}>
            <Suspense fallback={<PageLoader />}>
              <div className="animate-fade-in">
                <Routes>
                  {/* ── Public Routes ── */}
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password/:token" element={<ResetPassword />} />
                  <Route path="/search" element={<SearchResults />} />
                  <Route path="/salon/:id" element={<SalonDetails />} />
                  <Route path="/category/:type" element={<CategoryResult />} />
                  <Route path="/pro" element={<ProPage />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />


                  {/* ── Client Portal ── */}
                  <Route element={<ClientLayout />}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/appointments" element={<Appointments />} />
                    <Route path="/rewards" element={<Rewards />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/client/payment" element={<PaymentPage />} />
                    <Route path="/client/payment-success" element={<PaymentSuccess />} />
                    <Route path="/client/payment-simulate" element={<PaymentSimulate />} />
                    <Route path="/client/payment-history" element={<PaymentHistory />} />
                    <Route path="/notifications" element={<ClientNotifications />} />
                    <Route path="/client/review/:appointmentId" element={<ReviewPage />} />
                  </Route>

                  {/* ── Pro Portal ── */}
                  <Route element={<ProLayout />}>
                    <Route path="/pro/dashboard" element={<ProDashboard />} />
                    <Route path="/pro/salon" element={<MySalons />} />
                    <Route path="/pro/services" element={<MyServices />} />
                    <Route path="/pro/appointments" element={<ProAppointments />} />
                    <Route path="/pro/clients" element={<ProClients />} />
                    <Route path="/pro/employees" element={<ProEmployees />} />
                    <Route path="/pro/verification" element={<ProVerificationStatus />} />
                    <Route path="/pro/payment-settings" element={<ProPaymentSettings />} />
                    <Route path="/pro/notifications" element={<ProNotifications />} />
                    <Route path="/pro/reviews" element={<ProReviews />} />
                  </Route>

                  {/* ── Admin Portal ── */}
                  <Route element={<AdminLayout />}>
                    <Route path="/admin/salons" element={<AdminSalons />} />
                    <Route path="/admin/dashboard" element={<AdminDashboard />} />
                    <Route path="/admin/all-salons" element={<AdminSalons />} />
                    <Route path="/admin/users" element={<AdminUsers />} />
                    <Route path="/admin/appointments" element={<AdminAppointments />} />
                    <Route path="/admin/payments" element={<AdminPayments />} />
                    <Route path="/admin/reports" element={<AdminReports />} />
                    <Route path="/admin/marketing" element={<AdminMarketing />} />
                    <Route path="/admin/audit-logs" element={<AdminAuditLogs />} />
                  </Route>

                  {/* ── 404 Catch-All ── */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </div>
            </Suspense>
          </div>
        </ErrorBoundary>
      </SalonProvider>
    </AuthProvider>
  );
}

export default App;
